import { ANDROID_URL, IOS_APPSTORE_URL, IOS_TESTFLIGHT_URL, trackDownload } from '../lib/appLinks'
import { AppleLogo } from './AppleLogo'

/**
 * StoreBadges — download CTAs for the marketing site. Both apps are LIVE on the
 * public stores, so both official badges are shown:
 *   • ANDROID → "Get it on Google Play"
 *   • iOS     → "Download on the App Store" (approved 2026-07-12)
 * The official SVGs are self-hosted under /public/badges and used verbatim per
 * Google/Apple brand guidelines. TestFlightButton is retained only for the
 * early-access "Become a Tester" flow. Every CTA fires the download-funnel
 * beacon (trackDownload) on tap.
 */
export function GooglePlayBadge({ height = 52 }: { height?: number }) {
  return (
    <a
      href={ANDROID_URL}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackDownload('android')}
      aria-label="Get BimaUncle on Google Play"
      className="inline-flex transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-lg"
    >
      <img
        src="/badges/google-play-badge.svg"
        alt="Get it on Google Play"
        height={height}
        style={{ height, width: 'auto', display: 'block' }}
      />
    </a>
  )
}

/**
 * TestFlightButton — the iOS path while the app is in TestFlight beta.
 * Height-matched to the Play badge so the two line up in a row. Links to the
 * public TestFlight invite (tap → TestFlight app → install); the #tester form
 * remains the "register / get notified" path.
 */
export function TestFlightButton({ height = 52 }: { height?: number }) {
  return (
    <a
      href={IOS_TESTFLIGHT_URL}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackDownload('ios')}
      aria-label="Join the BimaUncle iOS beta on TestFlight"
      style={{ height }}
      className="inline-flex items-center gap-2.5 rounded-xl border border-black/15 bg-black px-4 text-left text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <AppleLogo size={20} className="shrink-0" />
      <span className="flex flex-col leading-tight">
        <span className="text-[11px] text-white/70">iPhone — TestFlight beta</span>
        <span className="text-sm font-semibold">Join the beta</span>
      </span>
    </a>
  )
}

/**
 * AppStoreBadge — the official "Download on the App Store" artifact, linking to
 * the live App Store listing (IOS_APPSTORE_URL). Optional caption below.
 */
export function AppStoreBadge({
  height = 52,
  caption = null,
}: {
  height?: number
  caption?: string | null
}) {
  return (
    <span className="inline-flex flex-col items-center gap-1">
      <a
        href={IOS_APPSTORE_URL}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackDownload('ios')}
        aria-label="Download BimaUncle on the App Store"
        className="inline-flex transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-lg"
      >
        <img
          src="/badges/app-store-badge.svg"
          alt="Download on the App Store"
          height={height}
          style={{ height, width: 'auto', display: 'block' }}
        />
      </a>
      {caption && (
        <span className="text-[11px] font-medium uppercase tracking-wide text-ink-soft">
          {caption}
        </span>
      )}
    </span>
  )
}

/** The "get the app" row: the two official store badges (Play + App Store). */
export function StoreBadgeRow({
  height = 52,
  className = '',
}: {
  height?: number
  className?: string
}) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <GooglePlayBadge height={height} />
      <AppStoreBadge height={height} />
    </div>
  )
}
